import { useMemo, useState, type ReactNode } from 'react';
import { ArrowDownAZ, ArrowUpAZ, ChevronDown, ChevronUp } from 'lucide-react';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  sortValue?: (row: T) => string | number | boolean | undefined;
  align?: 'left' | 'right';
  width?: string;
  /**
   * Plain-text column name. Needed when `header` is not a string, because on a
   * phone the header row is hidden and this label is printed beside the value.
   */
  label?: string;
  /** Renders full width as the card heading on a phone, with no label. */
  primary?: boolean;
}

interface Props<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  defaultSort?: { key: string; dir: 'asc' | 'desc' };
  dense?: boolean;
  refetching?: boolean;
  /**
   * Draws this many rows, and more on request, so a long list stays quick to
   * draw and to refresh on a phone. Sorting still covers every row.
   */
  pageSize?: number;
}

function labelOf<T>(column: Column<T>): string {
  if (column.label !== undefined) return column.label;
  return typeof column.header === 'string' ? column.header : '';
}

export function DataTable<T>({ columns, rows, rowKey, onRowClick, empty, defaultSort, dense, refetching, pageSize }: Props<T>) {
  const [sort, setSort] = useState(defaultSort);
  // Kept across refreshes and filter changes, so a list opened up stays open.
  const [shown, setShown] = useState(pageSize ?? Infinity);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const sv = col.sortValue;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = sv(a);
      const vb = sv(b);
      if (va === vb) return 0;
      if (va === undefined) return 1;
      if (vb === undefined) return -1;
      return (va < vb ? -1 : 1) * dir;
    });
  }, [rows, sort, columns]);

  const visible = sorted.length > shown ? sorted.slice(0, shown) : sorted;
  const hidden = sorted.length - visible.length;

  const toggleSort = (key: string) =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));

  const sortable = columns.filter((c) => c.sortValue);

  return (
    <div className={`table-wrap${refetching ? ' refetching' : ''}`}>
      {sortable.length > 0 ? (
        <div className="table-sort">
          <label className="table-sort__label" htmlFor={`sort-${sortable[0]!.key}`}>
            Sort by
          </label>
          <select
            id={`sort-${sortable[0]!.key}`}
            className="select"
            value={sort?.key ?? ''}
            onChange={(e) => setSort(e.target.value ? { key: e.target.value, dir: sort?.dir ?? 'asc' } : undefined)}
          >
            <option value="">Default order</option>
            {sortable.map((c) => (
              <option key={c.key} value={c.key}>
                {labelOf(c) || c.key}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn--icon"
            disabled={!sort}
            aria-label={sort?.dir === 'desc' ? 'Sort ascending' : 'Sort descending'}
            onClick={() => setSort((s) => (s ? { key: s.key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : s))}
          >
            {sort?.dir === 'desc' ? <ArrowUpAZ size={16} /> : <ArrowDownAZ size={16} />}
          </button>
        </div>
      ) : null}
      <table className={`table${dense ? ' table--dense' : ''}`}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className={`${c.sortValue ? 'sortable' : ''} ${c.align === 'right' ? 'num' : ''}`}
                style={c.width ? { width: c.width } : undefined}
                onClick={c.sortValue ? () => toggleSort(c.key) : undefined}
                aria-sort={sort?.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
              >
                <span className="row" style={{ display: 'inline-flex', gap: 4 }}>
                  {c.header}
                  {sort?.key === c.key ? (sort.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />) : null}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>{empty ?? <div className="empty">Nothing to show</div>}</td>
            </tr>
          ) : (
            visible.map((row) => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? 'clickable' : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={[c.align === 'right' ? 'num' : '', c.primary ? 'td--primary' : '']
                      .filter(Boolean)
                      .join(' ') || undefined}
                    data-label={c.primary ? undefined : labelOf(c) || undefined}
                  >
                    <span className="td__value">{c.render(row)}</span>
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {pageSize && hidden > 0 ? (
        <div className="table-more">
          <span className="small muted">
            Showing {visible.length.toLocaleString()} of {sorted.length.toLocaleString()}
          </span>
          <button type="button" className="btn btn--sm" onClick={() => setShown((n) => n + pageSize)}>
            Show {Math.min(pageSize, hidden).toLocaleString()} more
          </button>
          {hidden > pageSize ? (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShown(Infinity)}>
              Show all
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
